import React, { useState, useEffect, useMemo } from "react";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import FormSelect from "Common/Custom/FormSelect";
import DataTable from "Common/DataTable/DataTable";
import { getAttendanceReport } from "adminApi/AttendanceReportApi/AttendanceReport";
import { getAllEmployees } from "adminApi/HumanResources/Attendance";
import { toast } from "react-hot-toast";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "./json/AttendanceReport.json";

const AttendanceReport = () => {
    const [filters, setFilters] = useState({
        startDate: "",
        endDate: "",
        employeeId: "",
        startTime: "",
        endTime: ""
    });

    const [employees, setEmployees] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const { tabledata } = fields;

    // Fetch employees for dropdown
    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const res = await getAllEmployees();
                if (res.data.success) {
                    const options = res.data.data.map(emp => ({
                        value: emp.employee_id,
                        label: `${emp.first_name} ${emp.last_name}`
                    }));
                    setEmployees(options);
                }
            } catch (err) {
                console.error("Error fetching employees", err);
            }
        };
        fetchEmployees();
    }, []);

    // Formatters
    const formatTime = (isoString) => {
        if (!isoString || isoString === "---") return "---";
        const date = new Date(isoString);
        return date.toLocaleTimeString('en-GB', { hour12: false });
    };

    const formatDuration = (value) => {
        if (!value || value === '00:00:00') return "---";
        return value;
    };

    // Pre-process data for useTableConfig
    const processedReportData = useMemo(() => {
        return reportData.map(item => ({
            ...item,
            sign_in_time: formatTime(item.sign_in),
            sign_out_time: formatTime(item.sign_out),
            duration: formatDuration(item.total_hours)
        }));
    }, [reportData]);

    const displayFields = useMemo(() => generateDisplayFields(tabledata), [tabledata]);

    // Table Hook integration
    const { formattedData, columns } = useTableConfig({
        data: processedReportData,
        page: 1,      // Fixed for report if pagination is off
        limit: 1000,   // High limit for one-page list
        canShowAction: false,
        config: {
            entityName: "Attendance Report",
            idKey: "id",
            displayFields,
        },
    });

    const onFormChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const res = await getAttendanceReport(filters);
            if (res.success) {
                setReportData(res.data);
            } else {
                toast.error(res.message || "Failed to fetch report");
            }
        } catch (err) {
            toast.error("An error occurred while fetching report");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFilters({
            startDate: "",
            endDate: "",
            employeeId: "",
            startTime: "",
            endTime: ""
        });
        setReportData([]);
    };

    return (
        <PageBody headerchildren={<HeadingOne>Attendance Report</HeadingOne>}>
            <div className="card shadow-sm mb-4 border-0">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <FormDatePicker
                            field={{ name: "startDate", label: "Start Date", fieldSize: "col-xl-4 col-lg-3 col-md-6", placeholder: "Select Start Date" }}
                            value={filters.startDate}
                            handleChange={onFormChange}
                        />
                        <FormDatePicker
                            field={{ name: "endDate", label: "End Date", fieldSize: "col-xl-4 col-lg-3 col-md-6", placeholder: "Select End Date" }}
                            value={filters.endDate}
                            handleChange={onFormChange}
                        />
                        <FormSelect
                            field={{ name: "employeeId", label: "Employee", fieldSize: "col-xl-4 col-lg-3 col-md-6", placeholder: "Select Employee" }}
                            value={filters.employeeId}
                            options={employees}
                            handleChange={onFormChange}
                            isClearable
                        />
                        <FormDatePicker
                            field={{ name: "startTime", label: "Check-In After", fieldSize: "col-xl-4 col-lg-3 col-md-6", placeholder: "Min Sign-in Time" }}
                            value={filters.startTime}
                            handleChange={onFormChange}
                            mode="time"
                            options={{
                                enableTime: true,
                                noCalendar: true,
                                dateFormat: "H:i:S",
                                enableSeconds: true
                            }}
                        />
                        <FormDatePicker
                            field={{ name: "endTime", label: "Check-In Before", fieldSize: "col-xl-4 col-lg-3 col-md-6", placeholder: "Max Sign-in Time" }}
                            value={filters.endTime}
                            handleChange={onFormChange}
                            mode="time"
                            options={{
                                enableTime: true,
                                noCalendar: true,
                                dateFormat: "H:i:S",
                                enableSeconds: true
                            }}
                        />
                    </div>
                    <div className="row align-items-end justify-content-end mt-3">
                        <div className="col-lg-3 col-md-6">
                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn btn-primary" onClick={fetchReport} disabled={loading}>
                                    {loading ? "Searching..." : "Search"}
                                </button>
                                <button className="btn btn-outline-secondary shadow-none" onClick={handleReset}>
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card-body p-0">
                <DataTable
                    columns={columns}
                    data={formattedData}
                    options={{
                        loading,
                        fileName: "Attendance_Report",
                        copy: true,
                        csv: true,
                        pdf: true,
                        print: true
                    }}
                    pagination={{ isActive: false }}
                />
            </div>
        </PageBody>
    );
};

export default AttendanceReport;
